// eslint-disable-next-line max-classes-per-file
import { formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatChipsModule } from '@angular/material/chips';
import { TableConfigService } from '@core/services/table/table-config.service';
import { TranslationStoreService } from '@ogs-gmbh/ngx-translate';
import { LanguageSelectionService } from '@shared/components/language-selection/services/language-selection/language-selection.service';
import { TableFilterGroup } from '@shared/components/table-implementation/types/filter/table-filter-group.type';
import { TABLE_FILTER_TYPES, TableFilter } from '@shared/components/table-implementation/types/filter/table-filter.type';
import { TableConfig } from '@shared/components/table-implementation/types/table-config';
import { from, of, switchMap } from 'rxjs';
import { OverviewSettingsMenuService } from '../../services/overview-settings-menu.service';

// eslint-disable-next-line @tseslint/no-extraneous-class
export abstract class OverviewHeaderFilterChipsToken {}

interface FilterChip {
  displayName: string;
}

/**
 * Add this Component to {@link OverviewHeaderComponent | app-overview-header} to display the filter-chips that represent the provided TableConfigs filter
 *
 * @example
 *
 * ```html
 * <app-overview-header>
 *   <!-- other overview-header child-components... -->
 *   <app-overview-header-filter-chips />
 *   <!-- other overview-header child-components... -->
 * </app-overview-header>
 * ```
 */
@Component({
  selector: 'app-overview-header-filter-chips',
  standalone: true,
  imports: [ MatChipsModule ],
  templateUrl: './overview-header-filter-chips.component.html',
  styleUrl: './overview-header-filter-chips.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OverviewHeaderFilterChipsComponent extends OverviewHeaderFilterChipsToken {
  private readonly _tableConfigService: TableConfigService = inject(TableConfigService);

  private readonly _overviewSettingsMenuService: OverviewSettingsMenuService = inject(OverviewSettingsMenuService);

  private readonly _languageSelectionService: LanguageSelectionService = inject(LanguageSelectionService);

  private readonly _translationStoreService: TranslationStoreService = inject(TranslationStoreService);

  protected readonly chips: Signal<FilterChip[]> = toSignal(
    this._tableConfigService.selectedConfig$.pipe(
      switchMap((config: TableConfig) => {
        if (!config.filterConfig)
          return of<FilterChip[]>([]);

        const filters: TableFilter[] = config.filterConfig.filterGroups
          .flatMap((group: TableFilterGroup) => group.children);
        const chipPromises: Array<Promise<FilterChip | undefined>> = filters.map(async (filter: TableFilter) =>
          await this.parseToFilterChip(filter));

        return from(
          Promise.all(chipPromises).then((chips: Array<FilterChip | undefined>) =>
            chips.filter((chip: FilterChip | undefined): chip is FilterChip => chip !== undefined))
        );
      })
    ),
    { initialValue: [] }
  );

  private async parseToFilterChip (filter: TableFilter): Promise<FilterChip | undefined> {
    switch (filter.filterValue.filterType) {
      case TABLE_FILTER_TYPES.START_DATE:

      // eslint-disable-next-line no-fallthrough
      case TABLE_FILTER_TYPES.END_DATE: {
        if (filter.filterValue.date) {
          const formattedDate: string = formatDate(filter.filterValue.date, "shortDate", this._translationStoreService.getCurrentLocale().value);

          return {
            displayName: await this._languageSelectionService.getTranslatedTemplateLiteral`${ filter.filterNameTranslationInfo } - ${ formattedDate }`
          };
        }

        break;
      }

      case TABLE_FILTER_TYPES.SELECT: {
        if (filter.filterValue.isSelected) {
          return {
            displayName: await this._languageSelectionService.getTranslatedTemplateLiteral`${ filter.filterNameTranslationInfo ?? filter.filterName }`
          };
        }

        break;
      }
    }

    return undefined;
  }

  protected openMenu (): void {
    this._overviewSettingsMenuService.openMenu();
  }
}
